import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { IgLink } from "@intelligems/ig-theme/IgLink";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { IgTypographyTooltip } from "@intelligems/ig-theme/IgTypographyTooltip";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { ModKeys } from "@intelligems/ig-types/fe-only/personalizations";
import { OfferDto } from "@intelligems/ig-types/dtos/offers";
import { useGetGitBookContent } from "@src/api/gitbook/get-gitbook-content";
import {
  AdminRoutes,
  BillingRoutes,
  StandardRoutes,
} from "@src/components/Utils/routes";
import {
  getGitBookRouteParamsFromPathname,
  isGetInspiredYouTubePath,
} from "@src/routes/getInspiredRouteParams";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { useSelector } from "@src/store";
import { selectedActivatedModArray } from "@src/store/slices/experienceInfoState";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedOffers } from "@src/store/slices/offers";

export const getExperienceParentRoute = (
  experience: ExperienceFe,
  activatedMods: ModKeys[],
  offers: OfferDto[]
) => {
  const routes = StandardRoutes();
  const testTypes = experience.testTypes;
  const hasMod = (...keys: ModKeys[]) =>
    keys.some((key) => activatedMods.includes(key));

  // Onsite upsells are a merchandising type. Checked before content because new
  // experiments seed `hasTestContent: true` regardless of type, which would
  // otherwise send an upsell to the content table.
  if (testTypes?.hasTestOnsiteUpsell || hasMod("onsiteUpsell")) {
    return routes.merchandising;
  }
  // Same for post-purchase, and a saved experiment also gets the onsite edit
  // mods. Its type covers a test whose offer isn't added yet.
  if (
    experience.type === "postPurchase" ||
    testTypes?.hasTestPostPurchase ||
    hasMod("postPurchase", "postPurchasePlaceholder")
  ) {
    return routes.postPurchase;
  }
  if (
    testTypes?.hasTestCheckoutBlocks ||
    hasMod("checkoutBlock", "checkoutBlockPlaceholder", "shippingProgressBar")
  ) {
    return routes.checkout;
  }
  if (
    testTypes?.hasTestContent ||
    testTypes?.hasTestContentOnsite ||
    testTypes?.hasTestContentUrl ||
    testTypes?.hasTestContentAdvanced ||
    testTypes?.hasTestContentTheme ||
    testTypes?.hasTestContentTemplate ||
    testTypes?.hasTestOnsiteInjections ||
    hasMod("splitUrl", "theme", "template", "onsiteEdits", "onsiteInjections")
  ) {
    return routes.content;
  }
  if (testTypes?.hasTestPricing || hasMod("price", "priceStayAi")) {
    return routes.pricing;
  }
  if (testTypes?.hasTestShipping || hasMod("shipping", "freeShipping")) {
    return routes.shipping;
  }
  if (
    testTypes?.hasTestCampaign ||
    hasMod("offer", "tieredDiscount", "cartDiscount", "freeGift")
  ) {
    // Free-shipping offers live in the shipping table, not merchandising
    return experience.variations?.some(
      (variation) => variation.offer?.discountApplicationType === "freeShipping"
    ) ||
      offers.some((offer) => offer.discountApplicationType === "freeShipping")
      ? routes.shipping
      : routes.merchandising;
  }
  return routes.home;
};

const breadcrumbObj = {
  noChildren: [
    StandardRoutes().home.path,
    StandardRoutes().eventsManager.path,
    StandardRoutes().widgetLibrary.path,
    StandardRoutes().integrations.path,
    StandardRoutes().settings.path,
    AdminRoutes().configCompare.path,
    AdminRoutes().performance.path,
  ],
  billingRoutes: [BillingRoutes.agency.path, BillingRoutes.overview.path],
};

export const useBreadcrumbs = (): React.ReactNode[] => {
  const experience = useSelector(selectedExperience);
  const activatedMods = useSelector(selectedActivatedModArray);
  const offers = useSelector(selectedOffers);
  const { pathname } = useLocation();

  const gitBookRouteParams = useMemo(
    () => getGitBookRouteParamsFromPathname(pathname),
    [pathname]
  );
  const isGitBookRoute = Boolean(gitBookRouteParams);
  const { data: gitBookContent } = useGetGitBookContent(gitBookRouteParams);

  const navigate = useNavigate();

  return useMemo(() => {
    if (breadcrumbObj.noChildren.includes(pathname)) {
      return [];
    }
    if (breadcrumbObj.billingRoutes.includes(pathname)) {
      return [
        <IgTypography key={1}>Billing</IgTypography>,
        <IgTypography key={2}>
          {
            BillingRoutes[
              pathname === BillingRoutes.overview.path ? "overview" : "agency"
            ].sidebarName
          }
        </IgTypography>,
      ];
    }
    if (pathname === GENERAL_ROUTES.GetHelp) {
      return [
        <IgTypography
          onClick={() => void navigate(GENERAL_ROUTES.Home)}
          sx={{ cursor: "pointer" }}
          key={1}
        >
          Home
        </IgTypography>,
        <IgTypography key={2}>Get Help</IgTypography>,
      ];
    }
    if (isGetInspiredYouTubePath(pathname)) {
      return [
        <IgTypography
          onClick={() => void navigate(GENERAL_ROUTES.GetInspired)}
          sx={{ cursor: "pointer" }}
          key={1}
        >
          Ideas & Inspiration
        </IgTypography>,
        <IgTypography key={2}>Video</IgTypography>,
      ];
    }
    if (isGitBookRoute) {
      return [
        <IgTypography
          onClick={() => void navigate(GENERAL_ROUTES.GetInspired)}
          sx={{ cursor: "pointer" }}
          key={1}
        >
          Ideas & Inspiration
        </IgTypography>,
        <IgTypography key={2}>
          {gitBookContent?.title ?? "Documentation"}
        </IgTypography>,
      ];
    }
    if (experience) {
      const parentRoute = getExperienceParentRoute(
        experience,
        activatedMods,
        offers
      );
      return [
        <IgTypography
          onClick={() => void navigate(parentRoute.path)}
          sx={{ cursor: "pointer" }}
          key={1}
        >
          {parentRoute.sidebarName}
        </IgTypography>,
        <IgTypographyTooltip
          key={2}
          textMaxWidth={"300px"}
          textWidth={"100%"}
          variant={"body1"}
          textSx={{
            fontWeight: 400,
          }}
          text={experience.name}
        />,
      ];
    }
    return [];
  }, [
    activatedMods,
    experience,
    gitBookContent?.title,
    isGitBookRoute,
    navigate,
    offers,
    pathname,
  ]);
};
